// MonitoredCompaniesAdmin.jsx
//
// Bangert-only admin modal — add/edit/remove monitored companies, trigger
// manual runs. Every action requires DRL_ADMIN_KEY (server's existing
// requireAdminKey middleware). The key is prompted on first use this session
// and cached in sessionStorage so the user only types it once per tab.
//
// Password field is write-only: the API never returns stored passwords.
// Leave blank when editing to keep the existing password.

import React, { useEffect, useState, useCallback } from 'react';

const ADMIN_KEY_STORAGE = 'drl5k_admin_key';

const EMPTY_FORM = {
  companyId: '', displayName: '', userId: '', password: '',
  ccEmail: '', enabled: true,
};

const MonitoredCompaniesAdmin = ({ isOpen, onClose, apiBase }) => {
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem(ADMIN_KEY_STORAGE) || '');
  const [keyPromptOpen, setKeyPromptOpen] = useState(false);
  const [keyInput, setKeyInput] = useState('');
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  // Prompt for admin key if missing when modal opens
  useEffect(() => {
    if (isOpen && !adminKey) setKeyPromptOpen(true);
  }, [isOpen, adminKey]);

  const apiCall = useCallback(async (path, opts = {}) => {
    const res = await fetch(`${apiBase}${path}`, {
      ...opts,
      headers: {
        'Content-Type': 'application/json',
        'x-admin-key': adminKey,
        ...(opts.headers || {}),
      },
    });
    const data = await res.json().catch(() => ({}));
    if (res.status === 403) {
      // Bad key — clear and re-prompt
      sessionStorage.removeItem(ADMIN_KEY_STORAGE);
      setAdminKey('');
      setKeyPromptOpen(true);
      throw new Error('Invalid admin key');
    }
    if (!res.ok) throw new Error(data.error || `${res.status}`);
    return data;
  }, [apiBase, adminKey]);

  const loadCompanies = useCallback(async () => {
    if (!adminKey) return;
    setLoading(true);
    try {
      const data = await apiCall('/api/usage-monitor/companies');
      setCompanies(data.companies || []);
    } catch (err) {
      setStatus({ type: 'err', msg: err.message });
    } finally {
      setLoading(false);
    }
  }, [adminKey, apiCall]);

  useEffect(() => { if (isOpen && adminKey) loadCompanies(); }, [isOpen, adminKey, loadCompanies]);

  const submitKey = () => {
    sessionStorage.setItem(ADMIN_KEY_STORAGE, keyInput.trim());
    setAdminKey(keyInput.trim());
    setKeyInput('');
    setKeyPromptOpen(false);
  };

  const save = async () => {
    try {
      await apiCall('/api/usage-monitor/companies', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      setForm(EMPTY_FORM);
      setEditing(false);
      setStatus({ type: 'ok', msg: 'Saved' });
      loadCompanies();
    } catch (err) {
      setStatus({ type: 'err', msg: err.message });
    }
  };

  const edit = (c) => {
    setForm({
      companyId: c.company_id,
      displayName: c.display_name,
      userId: c.user_id,
      password: '',
      ccEmail: c.cc_email || '',
      enabled: c.enabled === 1,
    });
    setEditing(true);
  };

  // confirmRemoveId is null when no confirmation is pending, or the company_id
  // currently waiting for confirmation. Inline confirm replaces window.confirm
  // because Office task panes block native dialogs.
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);

  const remove = async (companyId) => {
    try {
      await apiCall(`/api/usage-monitor/companies/${companyId}`, { method: 'DELETE' });
      setConfirmRemoveId(null);
      loadCompanies();
    } catch (err) {
      setStatus({ type: 'err', msg: err.message });
      setConfirmRemoveId(null);
    }
  };

  const runNow = async () => {
    try {
      await apiCall('/api/usage-monitor/run-now', { method: 'POST' });
      setStatus({ type: 'ok', msg: 'Nightly job started — check back in a minute.' });
    } catch (err) {
      setStatus({ type: 'err', msg: err.message });
    }
  };

  if (!isOpen) return null;

  // Admin key prompt
  if (keyPromptOpen) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
        <div className="bg-gray-800 rounded-lg shadow-xl border border-yellow-500/30 p-6 max-w-md w-full">
          <h2 className="text-sm font-bold text-yellow-400 mb-2">Admin Key Required</h2>
          <p className="text-xs text-gray-400 mb-4">
            Enter the value of <code className="bg-gray-900 px-1 rounded">DRL_ADMIN_KEY</code> to manage monitored companies.
            It will be remembered for this browser session.
          </p>
          <input
            type="password"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submitKey()}
            autoFocus
            className="w-full bg-gray-900 border border-gray-700 text-gray-200 text-xs rounded px-3 py-2 mb-4"
            placeholder="paste admin key…"
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => { setKeyPromptOpen(false); onClose(); }}
              className="px-3 py-1.5 text-xs bg-gray-700 text-gray-300 rounded hover:bg-gray-600"
            >Cancel</button>
            <button
              onClick={submitKey}
              disabled={!keyInput.trim()}
              className="px-3 py-1.5 text-xs bg-yellow-500 text-gray-900 font-bold rounded hover:bg-yellow-400 disabled:opacity-50"
            >Continue</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-gray-800 rounded-lg shadow-xl border border-cyan-500/30 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 flex items-center justify-between">
          <h2 className="text-sm font-bold text-cyan-400 uppercase">Manage Monitored Companies</h2>
          <div className="flex gap-2">
            <button
              onClick={runNow}
              className="px-3 py-1.5 text-[11px] bg-gray-700 text-gray-300 rounded hover:bg-gray-600"
            >Run nightly job now</button>
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-[11px] bg-gray-700 text-gray-300 rounded hover:bg-gray-600"
            >Close</button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {status && (
            <div className={`p-2 rounded text-xs ${
              status.type === 'ok' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'
                                   : 'bg-red-500/10 text-red-400 border border-red-500/30'
            }`}>{status.msg}</div>
          )}

          {/* Companies table */}
          <div className="bg-gray-900 rounded border border-gray-700 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-gray-800">
                <tr className="text-left text-gray-500">
                  <th className="px-3 py-2 font-medium">Company ID</th>
                  <th className="px-3 py-2 font-medium">Name</th>
                  <th className="px-3 py-2 font-medium">User</th>
                  <th className="px-3 py-2 font-medium">CC Email</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="px-3 py-4 text-center text-gray-500">Loading…</td></tr>
                )}
                {!loading && companies.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-4 text-center text-gray-500">No monitored companies yet.</td></tr>
                )}
                {!loading && companies.map((c) => (
                  <tr key={c.company_id} className="border-t border-gray-800">
                    <td className="px-3 py-2 font-mono text-cyan-400">{c.company_id}</td>
                    <td className="px-3 py-2 text-gray-200">{c.display_name}</td>
                    <td className="px-3 py-2 text-gray-400">{c.user_id}</td>
                    <td className="px-3 py-2 text-gray-400">{c.cc_email || '—'}</td>
                    <td className="px-3 py-2">
                      {c.enabled
                        ? <span className="text-emerald-400">enabled</span>
                        : <span className="text-gray-500">disabled</span>}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {confirmRemoveId === c.company_id ? (
                        <>
                          <span className="text-[10px] text-gray-400 mr-2">remove?</span>
                          <button
                            onClick={() => remove(c.company_id)}
                            className="text-red-400 hover:text-red-300 mr-2 font-bold"
                          >yes</button>
                          <button
                            onClick={() => setConfirmRemoveId(null)}
                            className="text-gray-400 hover:text-gray-300"
                          >no</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => edit(c)} className="text-cyan-400 hover:text-cyan-300 mr-3">edit</button>
                          <button onClick={() => setConfirmRemoveId(c.company_id)} className="text-red-400 hover:text-red-300">remove</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Add / edit form */}
          <div className="bg-gray-900 rounded border border-gray-700 p-4">
            <h3 className="text-xs font-bold text-gray-400 uppercase mb-3">
              {editing ? `Edit ${form.companyId}` : 'Add company'}
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <Field label="Company ID" value={form.companyId} disabled={editing}
                     onChange={(v) => setForm({ ...form, companyId: v })} />
              <Field label="Display name" value={form.displayName}
                     onChange={(v) => setForm({ ...form, displayName: v })} />
              <Field label="Web services user ID" value={form.userId}
                     onChange={(v) => setForm({ ...form, userId: v })} />
              <Field label={editing ? 'Password (blank = keep existing)' : 'Password'}
                     type="password" value={form.password}
                     onChange={(v) => setForm({ ...form, password: v })} />
              <Field label="CC email (optional)" value={form.ccEmail}
                     onChange={(v) => setForm({ ...form, ccEmail: v })} />
              <label className="flex items-center gap-2 mt-5 text-gray-300">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                  className="rounded bg-gray-700 border-gray-600"
                />
                Enabled
              </label>
            </div>
            <div className="mt-4 flex gap-2">
              <button
                onClick={save}
                className="px-4 py-1.5 text-xs bg-cyan-500 text-gray-900 font-bold rounded hover:bg-cyan-400"
              >{editing ? 'Save changes' : 'Add company'}</button>
              {editing && (
                <button
                  onClick={() => { setForm(EMPTY_FORM); setEditing(false); }}
                  className="px-4 py-1.5 text-xs bg-gray-700 text-gray-300 rounded hover:bg-gray-600"
                >Cancel</button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', disabled = false }) => (
  <label className="flex flex-col">
    <span className="text-gray-500 text-[10px] uppercase mb-1">{label}</span>
    <input
      type={type}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className="bg-gray-800 border border-gray-700 text-gray-200 rounded px-2 py-1.5 disabled:opacity-50"
    />
  </label>
);

export default MonitoredCompaniesAdmin;